import React, { useEffect, useRef, useState } from "react";
import { makeStyles, useTheme } from "@seahorse/ui";
import FastForexPriceChartTileComponent from "../Components/FastForexPriceChartTile";
import CoTraderForwardChart from "../Components/CoTraderForwardChartTileComponent";
import CoTraderInsightsComponent from "../Components/CoTraderInsightsComponent";
import CoTraderChatTileComponent from "../Components/CoTraderChatTileComponent";
import CoTraderExecutionDecisionComponent from "../Components/CoTraderExecutionDecisionComponent";
import CoTraderRealtimeSentimentComponent from "../Components/CoTraderRealtimeSentimentComponent";
import PopOverMenu from "../../PopOverMenu";
import {
  clampChartHeight,
  getPreferredChartHeight,
  savePreferredChartHeight,
  setSessionVariable,
  useInsightsWs,
} from "../Utils/CoTraderUtils";

interface Context {
  id?: {
    [key: string]: string;
  };
  name?: string;
  type: string;
  [s: string]: any;
}

type MarketTab = "split" | "spot" | "forward";
type ContentTab = "insights" | "execution" | "chat";
const WIDE_FX_LAYOUT_QUERY = "(min-width: 1100px)";
const COMPACT_CHART_LAYOUT_QUERY = "(max-width: 580px)";

interface PreTradeTileProps {
  context: Context;
  currencyList: string[];
  initialMarketTab?: MarketTab;
  showRealtimeSentiment?: boolean;
  showExecutionDecision?: boolean;
  responsiveSideBySide?: boolean;
}

const PreTradeTile: React.FunctionComponent<PreTradeTileProps> = ({
  context,
  currencyList,
  initialMarketTab = "spot",
  showRealtimeSentiment = false,
  showExecutionDecision = false,
  responsiveSideBySide = false,
}) => {
  const theme = useTheme();
  const [currencyPair, setCurrencyPair] = useState("USDJPY");
  const [useContext, setUseContext] = useState(true);
  const [updateSpeed, setUpdateSpeed] = useState(3);
  const [tenorList, setTenorList] = useState([]);
  const [tenors, setTenors]: any = useState([]);
  const [userLanguage, setUserLanguage] = useState(
    localStorage.getItem("user_language") || "English"
  );
  const [activeMarketTab, setActiveMarketTab] =
    useState<MarketTab>(initialMarketTab);
  const [activeContentTab, setActiveContentTab] =
    useState<ContentTab>("insights");
  const [chartHeight, setChartHeight] = useState(getPreferredChartHeight);
  const [isCompactChartLayout, setIsCompactChartLayout] = useState(() =>
    typeof window !== "undefined" &&
    window.matchMedia(COMPACT_CHART_LAYOUT_QUERY).matches
  );
  const [hasVisitedChat, setHasVisitedChat] = useState(false);
  const [isWideLayout, setIsWideLayout] = useState(() =>
    typeof window !== "undefined" &&
    window.matchMedia(WIDE_FX_LAYOUT_QUERY).matches
  );

  const useStyles = makeStyles({
    container: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
      width: "100%",
      padding: "10px",
      backgroundColor: theme.globallink.colors.DarkGrey[500],
    },
    title: {
      color: "#E0E0E0",
      fontSize: "16px",
      fontWeight: "bolder",
      display: "flex",
      justifyContent: "space-between",
      padding: "4px",
      alignItems: "center",
    },
    tabBar: {
      display: "flex",
      gap: "16px",
      paddingBottom: "4px",
      borderBottom: "1px solid rgba(255,255,255,0.1)",
      flexWrap: "wrap",
    },
    marketTabBar: {
      marginTop: "10px",
    },
    workspaceTabBar: {
      marginTop: "6px",
    },
    tabButton: {
      border: 0,
      borderBottom: "2px solid transparent",
      borderRadius: 0,
      minHeight: "36px",
      padding: "7px 2px 8px",
      marginBottom: "-3px",
      background: "transparent",
      color: "rgba(224,224,224,0.74)",
      cursor: "pointer",
      fontWeight: 600,
      fontSize: "13px",
      lineHeight: "18px",
      transition: "all 0.15s ease",
      "&:hover": {
        background: "transparent",
        color: "#FFFFFF",
      },
      "&:focus-visible": {
        outline: `2px solid ${theme.globallink.colors.Blue[300]}`,
        outlineOffset: "3px",
      },
    },
    tabButtonActive: {
      borderBottomColor: theme.globallink.colors.Blue[500],
      color: "#FFFFFF",
      fontWeight: 700,
    },
    // Chart cards mirror BestXTile so both tiles render charts identically.
    chartRow: {
      display: "flex",
      gap: "12px",
      width: "100%",
      minHeight: 0,
      marginTop: "12px",
      flexShrink: 0,
    },
    chartCard: {
      flex: 1,
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      borderRadius: "12px",
      padding: "8px",
      border: "1px solid rgba(255,255,255,0.08)",
      background: "rgba(255,255,255,0.04)",
      overflow: "hidden",
    },
    chartCardBody: {
      flex: 1,
      minHeight: 0,
      minWidth: 0,
      overflow: "auto",
      borderRadius: "8px",
    },
    chartTitle: {
      color: "#E0E0E0",
      fontSize: "13px",
      fontWeight: 700,
      marginBottom: "8px",
      lineHeight: "18px",
    },
    chartResizeGrip: {
      width: "44px",
      height: "12px",
      margin: "8px auto 0",
      flexShrink: 0,
      position: "relative",
      cursor: "row-resize",
      touchAction: "none",
      "&::after": {
        content: '""',
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: "3px",
        borderRadius: "999px",
        background: "rgba(224,224,224,0.5)",
      },
      "&:hover::after": {
        background: "rgba(224,224,224,0.9)",
      },
    },
    compactWorkspace: {
      flex: 1,
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },
    compactWorkspaceTabBar: {
      marginBottom: "6px",
    },
    sentimentPanel: {
      flexShrink: 0,
      marginBottom: "6px",
    },
    wideSentimentPanel: {
      padding: "8px 8px 0",
    },
    tabPanel: {
      flex: 1,
      minHeight: 0,
      display: "flex",
      overflow: "hidden",
    },
    wideWorkspace: {
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)",
      gap: "12px",
      flex: 1,
      minHeight: 0,
      paddingTop: "10px",
    },
    wideWorkspacePane: {
      minWidth: 0,
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      border: "1px solid rgba(255,255,255,0.1)",
      borderRadius: "12px",
      background: "rgba(0,0,0,0.08)",
    },
    wideChatPane: {
      minWidth: 0,
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },
    widePaneHeader: {
      display: "flex",
      alignItems: "center",
      gap: "16px",
      minHeight: "46px",
      padding: "0 18px",
      borderBottom: "1px solid rgba(255,255,255,0.08)",
      color: "rgba(224,224,224,0.82)",
      fontSize: "14px",
      fontWeight: 700,
    },
    widePaneTabs: {
      display: "flex",
      alignItems: "stretch",
      gap: "24px",
      height: "46px",
    },
    widePaneTab: {
      border: 0,
      borderBottom: "2px solid transparent",
      marginBottom: "-1px",
      padding: "0 1px",
      background: "transparent",
      color: "rgba(224,224,224,0.68)",
      cursor: "pointer",
      fontSize: "14px",
      fontWeight: 600,
      "&:hover": {
        color: "#FFFFFF",
      },
      "&:focus-visible": {
        outline: `2px solid ${theme.globallink.colors.Blue[300]}`,
        outlineOffset: "2px",
      },
    },
    widePaneTabActive: {
      borderBottomColor: theme.globallink.colors.Blue[500],
      color: "#FFFFFF",
      fontWeight: 700,
    },
    widePaneContent: {
      flex: 1,
      minHeight: 0,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
    },
  });

  const classes = useStyles();
  const chartResizeStart = useRef<{
    pointerId: number;
    startY: number;
    startHeight: number;
  } | null>(null);
  const handleChartResize = (height: number) =>
    setChartHeight(savePreferredChartHeight(height));
  const getDraggedChartHeight = (
    event: React.PointerEvent<HTMLDivElement>
  ) => {
    const resizeStart = chartResizeStart.current;
    return resizeStart?.pointerId === event.pointerId
      ? clampChartHeight(
          resizeStart.startHeight + event.clientY - resizeStart.startY
        )
      : null;
  };
  const resizableChartProps = {
    style: {
      flex: "0 0 auto",
      height: `${chartHeight}px`,
      minHeight: "150px",
      maxHeight: "500px",
    },
  };
  const chartResizeGripProps = {
    title: "Drag to resize the chart",
    onPointerDown: (event: React.PointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) return;
      event.preventDefault();
      chartResizeStart.current = {
        pointerId: event.pointerId,
        startY: event.clientY,
        startHeight: chartHeight,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    onPointerMove: (event: React.PointerEvent<HTMLDivElement>) => {
      const height = getDraggedChartHeight(event);
      if (height !== null) setChartHeight(height);
    },
    onPointerUp: (event: React.PointerEvent<HTMLDivElement>) => {
      const height = getDraggedChartHeight(event);
      chartResizeStart.current = null;
      if (height !== null) handleChartResize(height);
    },
    onPointerCancel: () => {
      chartResizeStart.current = null;
    },
  };
  const isResponsiveWideLayout = responsiveSideBySide && isWideLayout;
  const sharedUpdatesStream = useInsightsWs({
    currencyPair,
    updateSpeed,
    userLanguage,
    showCountDownInsights: true,
    enabled: showRealtimeSentiment,
  });

  useEffect(() => {
    if (!useContext) {
      return;
    }

    try {
      const currencyPairReceived = context?.instruments?.[0]?.id?.ticker;
      if (currencyPairReceived && currencyList?.includes(currencyPairReceived)) {
        setCurrencyPair(currencyPairReceived);
      }
    } catch {
      console.log("Some Error Happened");
    }
  }, [context, currencyList, useContext]);

  useEffect(() => {
    setActiveMarketTab(initialMarketTab);
  }, [initialMarketTab]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const mediaQuery = window.matchMedia(COMPACT_CHART_LAYOUT_QUERY);
    const updateChartLayout = () => {
      setIsCompactChartLayout(mediaQuery.matches);
      if (mediaQuery.matches) {
        setActiveMarketTab((tab) =>
          tab === "split" ? initialMarketTab : tab
        );
      }
    };

    updateChartLayout();
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", updateChartLayout);
      return () => mediaQuery.removeEventListener("change", updateChartLayout);
    }

    mediaQuery.addListener(updateChartLayout);
    return () => mediaQuery.removeListener(updateChartLayout);
  }, [initialMarketTab]);

  useEffect(() => {
    if (!responsiveSideBySide || typeof window === "undefined") {
      setIsWideLayout(false);
      return undefined;
    }

    const mediaQuery = window.matchMedia(WIDE_FX_LAYOUT_QUERY);
    const updateLayout = () => setIsWideLayout(mediaQuery.matches);

    updateLayout();
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", updateLayout);
      return () => mediaQuery.removeEventListener("change", updateLayout);
    }

    mediaQuery.addListener(updateLayout);
    return () => mediaQuery.removeListener(updateLayout);
  }, [responsiveSideBySide]);

  useEffect(() => {
    if (!showExecutionDecision && activeContentTab === "execution") {
      setActiveContentTab("insights");
    }
  }, [activeContentTab, showExecutionDecision]);

  useEffect(() => {
    if (isResponsiveWideLayout && activeContentTab === "chat") {
      setActiveContentTab("insights");
    }
  }, [activeContentTab, isResponsiveWideLayout]);

  useEffect(() => {
    setSessionVariable(currencyPair);
  }, [currencyPair]);

  useEffect(() => {
    // The wide layout always shows chat; remember that so narrowing the
    // window keeps the mounted conversation instead of discarding it.
    if (isResponsiveWideLayout) {
      setHasVisitedChat(true);
    }
  }, [isResponsiveWideLayout]);

  const selectContentTab = (tab: ContentTab) => {
    if (tab === "chat") {
      setHasVisitedChat(true);
    }
    setActiveContentTab(tab);
  };

  const spotChart = (
    <FastForexPriceChartTileComponent
      currencyPair={currencyPair}
      chartHeight={chartHeight}
      historyPointCount={60}
      enableLiveUpdates
    />
  );
  const forwardChart = (
    <CoTraderForwardChart
      currencyPair={currencyPair}
      setTenorList={setTenorList}
      tenors={tenors}
      chartHeight={chartHeight}
    />
  );
  const marketTabs = (
    <div
      className={`${classes.tabBar} ${classes.marketTabBar}`}
      role="tablist"
      aria-label="Market data view"
    >
      {!isCompactChartLayout && (
        <button
          type="button"
          role="tab"
          id="pre-trade-market-tab-split"
          aria-controls="pre-trade-market-panel"
          className={`${classes.tabButton} ${
            activeMarketTab === "split" ? classes.tabButtonActive : ""
          }`}
          onClick={() => setActiveMarketTab("split")}
          aria-selected={activeMarketTab === "split"}
        >
          Split
        </button>
      )}
      <button
        type="button"
        role="tab"
        id="pre-trade-market-tab-spot"
        aria-controls="pre-trade-market-panel"
        className={`${classes.tabButton} ${
          activeMarketTab === "spot" ? classes.tabButtonActive : ""
        }`}
        onClick={() => setActiveMarketTab("spot")}
        aria-selected={activeMarketTab === "spot"}
      >
        Spot
      </button>
      <button
        type="button"
        role="tab"
        id="pre-trade-market-tab-forward"
        aria-controls="pre-trade-market-panel"
        className={`${classes.tabButton} ${
          activeMarketTab === "forward" ? classes.tabButtonActive : ""
        }`}
        onClick={() => setActiveMarketTab("forward")}
        aria-selected={activeMarketTab === "forward"}
      >
        Forward
      </button>
    </div>
  );
  const marketChart = (
    <div
      className={classes.chartRow}
      id="pre-trade-market-panel"
      role="tabpanel"
      aria-labelledby={`pre-trade-market-tab-${activeMarketTab}`}
    >
      {activeMarketTab === "split" ? (
        <>
          <div className={classes.chartCard}>
            <div className={classes.chartTitle}>Spot Prices</div>
            <div className={classes.chartCardBody} {...resizableChartProps}>
              {spotChart}
            </div>
          </div>
          <div className={classes.chartCard}>
            <div className={classes.chartTitle}>Forward Points</div>
            <div className={classes.chartCardBody} {...resizableChartProps}>
              {forwardChart}
            </div>
          </div>
        </>
      ) : (
        <div className={classes.chartCard}>
          <div className={classes.chartCardBody} {...resizableChartProps}>
            {activeMarketTab === "spot" ? spotChart : forwardChart}
          </div>
        </div>
      )}
    </div>
  );
  const sentimentPanel = showRealtimeSentiment ? (
    <div
      className={`${classes.sentimentPanel} ${
        isResponsiveWideLayout ? classes.wideSentimentPanel : ""
      }`}
    >
      <CoTraderRealtimeSentimentComponent
        currencyPair={currencyPair}
        updateSpeed={updateSpeed}
        userLanguage={userLanguage}
        useStreamData={true}
        streamSentiment={sharedUpdatesStream.sentiment}
        streamTimestamp={sharedUpdatesStream.sentimentUpdatedAt}
        streamLoading={sharedUpdatesStream.loading}
        compact
      />
    </div>
  ) : null;
  const insightsPanel = (
    <CoTraderInsightsComponent
      currencyPair={currencyPair}
      updateSpeed={updateSpeed}
      forward_tile={activeMarketTab === "forward"}
      useStreamData={showRealtimeSentiment}
      streamedInsights={sharedUpdatesStream.insights}
      streamedLoading={sharedUpdatesStream.loading}
      variant="preTrade"
    />
  );
  const executionPanel = showExecutionDecision ? (
    <CoTraderExecutionDecisionComponent
      currencyPair={currencyPair}
      language={userLanguage}
    />
  ) : null;
  const shouldRenderChat = isResponsiveWideLayout || hasVisitedChat;
  const chatPanel = (
    <CoTraderChatTileComponent currencyPair={currencyPair} variant="preTrade" />
  );

  return (
    <div className={classes.container}>
      <div className={classes.title}>
        <div>{currencyPair}</div>
        <div className="d-flex flex-row">
          <PopOverMenu
            currencyPair={currencyPair}
            setCurrencyPair={setCurrencyPair}
            setUpdateSpeed={setUpdateSpeed}
            setUseContext={setUseContext}
            currencyList={currencyList}
            showUpdateSpeed={true}
            tenorList={tenorList}
            setTenor={setTenors}
            setUserLanguage={setUserLanguage}
            userLanguage={userLanguage}
          />
        </div>
      </div>

      {marketTabs}
      {marketChart}
      <div className={classes.chartResizeGrip} {...chartResizeGripProps} />

      {/*
        Both layouts share one element tree so crossing the wide breakpoint
        re-styles the workspace instead of remounting it. The chat pane must
        keep its position here: remounting it drops the conversation and
        closes any in-flight answer stream.
      */}
      <div
        className={
          isResponsiveWideLayout
            ? classes.wideWorkspace
            : classes.compactWorkspace
        }
      >
        {isResponsiveWideLayout ? null : (
          <div
            className={`${classes.tabBar} ${classes.workspaceTabBar} ${classes.compactWorkspaceTabBar}`}
            role="tablist"
            aria-label="Market analysis view"
          >
            <button
              type="button"
              role="tab"
              id="pre-trade-content-tab-insights"
              aria-controls="pre-trade-insights-panel"
              className={`${classes.tabButton} ${
                activeContentTab === "insights" ? classes.tabButtonActive : ""
              }`}
              onClick={() => selectContentTab("insights")}
              aria-selected={activeContentTab === "insights"}
            >
              Insights
            </button>
            {showExecutionDecision ? (
              <button
                type="button"
                role="tab"
                id="pre-trade-content-tab-execution"
                aria-controls="pre-trade-execution-panel"
                className={`${classes.tabButton} ${
                  activeContentTab === "execution" ? classes.tabButtonActive : ""
                }`}
                onClick={() => selectContentTab("execution")}
                aria-selected={activeContentTab === "execution"}
              >
                Algo vs RFQ
              </button>
            ) : null}
            <button
              type="button"
              role="tab"
              id="pre-trade-content-tab-chat"
              aria-controls="pre-trade-chat-panel"
              className={`${classes.tabButton} ${
                activeContentTab === "chat" ? classes.tabButtonActive : ""
              }`}
              onClick={() => selectContentTab("chat")}
              aria-selected={activeContentTab === "chat"}
            >
              Ask EMAlpha
            </button>
          </div>
        )}
        {isResponsiveWideLayout ? null : sentimentPanel}

        <section
          className={
            isResponsiveWideLayout ? classes.wideWorkspacePane : classes.tabPanel
          }
          hidden={!isResponsiveWideLayout && activeContentTab === "chat"}
        >
          {isResponsiveWideLayout ? (
            <>
              <div className={classes.widePaneHeader}>
                {showExecutionDecision ? (
                  <div
                    className={classes.widePaneTabs}
                    role="tablist"
                    aria-label="Market analysis view"
                  >
                    <button
                      type="button"
                      role="tab"
                      className={`${classes.widePaneTab} ${
                        activeContentTab !== "execution"
                          ? classes.widePaneTabActive
                          : ""
                      }`}
                      onClick={() => setActiveContentTab("insights")}
                      aria-selected={activeContentTab !== "execution"}
                    >
                      Insights
                    </button>
                    <button
                      type="button"
                      role="tab"
                      className={`${classes.widePaneTab} ${
                        activeContentTab === "execution"
                          ? classes.widePaneTabActive
                          : ""
                      }`}
                      onClick={() => setActiveContentTab("execution")}
                      aria-selected={activeContentTab === "execution"}
                    >
                      Algo vs RFQ
                    </button>
                  </div>
                ) : (
                  <span>Insights</span>
                )}
              </div>
              <div className={classes.widePaneContent}>
                {sentimentPanel}
                <div className={classes.tabPanel}>
                  {activeContentTab === "execution" && executionPanel
                    ? executionPanel
                    : insightsPanel}
                </div>
              </div>
            </>
          ) : (
            <>
              <div
                className={classes.tabPanel}
                id="pre-trade-insights-panel"
                role="tabpanel"
                aria-labelledby="pre-trade-content-tab-insights"
                hidden={activeContentTab !== "insights"}
              >
                {insightsPanel}
              </div>
              {showExecutionDecision ? (
                <div
                  className={classes.tabPanel}
                  id="pre-trade-execution-panel"
                  role="tabpanel"
                  aria-labelledby="pre-trade-content-tab-execution"
                  hidden={activeContentTab !== "execution"}
                >
                  {executionPanel}
                </div>
              ) : null}
            </>
          )}
        </section>

        <section
          className={
            isResponsiveWideLayout ? classes.wideChatPane : classes.tabPanel
          }
          {...(isResponsiveWideLayout
            ? { "aria-label": "Chat" }
            : {
                id: "pre-trade-chat-panel",
                role: "tabpanel",
                "aria-labelledby": "pre-trade-content-tab-chat",
              })}
          hidden={!isResponsiveWideLayout && activeContentTab !== "chat"}
        >
          <div className={classes.widePaneContent}>
            {shouldRenderChat ? chatPanel : null}
          </div>
        </section>
      </div>
    </div>
  );
};

export default PreTradeTile;
